/**
 * Configuration for entity messages
 */
export interface EntityConfig {
  /** Entity name in singular/plural forms */
  name: { singular: string; plural: string };
  /** Italian article for the entity */
  article: { singular: string; plural: string };
  /** Localized messages for success and error scenarios */
  messages: {
    success: { created: string; updated: string; deleted: string };
    error: {
      notFound: string;
      conflict: string;
      validation: string;
      /** Custom error codes mapping to messages */
      custom?: Record<string, string>;
    };
  };
}

/**
 * Registry of all entities with their localized messages
 * Used by MessageBuilderService and ToastInterceptor
 */
export const ENTITY_REGISTRY = {
  users: {
    name: { singular: 'dipendente', plural: 'dipendenti' },
    article: { singular: 'il', plural: 'i' },
    messages: {
      success: {
        created: 'Dipendente creato con successo',
        updated: 'Dipendente aggiornato con successo',
        deleted: 'Dipendente eliminato con successo',
      },
      error: {
        notFound: 'Il dipendente non è stato trovato',
        conflict: 'Un dipendente con questa email è già presente',
        validation: 'I dati del dipendente non sono validi',
      },
    },
  },
  employees: {
    name: { singular: 'dipendente', plural: 'dipendenti' },
    article: { singular: 'il', plural: 'i' },
    messages: {
      success: {
        created: 'Dipendente creato con successo',
        updated: 'Dipendente aggiornato con successo',
        deleted: 'Dipendente eliminato con successo',
      },
      error: {
        notFound: 'Il dipendente non è stato trovato',
        conflict: 'Un dipendente con questa email è già presente',
        validation: 'I dati del dipendente non sono validi',
      },
    },
  },
  tenants: {
    name: { singular: 'azienda', plural: 'aziende' },
    article: { singular: 'l\'', plural: 'le' },
    messages: {
      success: {
        created: 'Azienda creata con successo',
        updated: 'Azienda aggiornata con successo',
        deleted: 'Azienda eliminata con successo',
      },
      error: {
        notFound: 'L\'azienda non è stata trovata',
        conflict: 'Un\'azienda con questo codice è già presente',
        validation: 'I dati dell\'azienda non sono validi',
        custom: {
          CONSULTANT_ALREADY_ASSIGNED: 'Questa azienda ha già un consulente del lavoro assegnato',
        },
      },
    },
  },
  laborConsultant: {
    name: { singular: 'consulente del lavoro', plural: 'consulenti del lavoro' },
    article: { singular: 'il', plural: 'i' },
    messages: {
      success: {
        created: 'Consulente del lavoro assegnato con successo',
        updated: 'Consulente del lavoro aggiornato con successo',
        deleted: 'Consulente del lavoro rimosso con successo',
      },
      error: {
        notFound: 'Il consulente del lavoro non è stato trovato',
        conflict: 'Un consulente del lavoro con questa email è già presente',
        validation: 'I dati del consulente del lavoro non sono validi',
        custom: {
          USER_NOT_LABOR_CONSULTANT: 'Questo utente esiste ma non è un consulente del lavoro',
        },
      },
    },
  },
  receipts: {
    name: { singular: 'nota spese', plural: 'note spese' },
    article: { singular: 'la', plural: 'le' },
    messages: {
      success: {
        created: 'Nota spese creata con successo',
        updated: 'Nota spese aggiornata con successo',
        deleted: 'Nota spese eliminata con successo',
      },
      error: {
        notFound: 'La nota spese non è stata trovata',
        conflict: 'Una nota spese con questo codice è già presente',
        validation: 'I dati della nota spese non sono validi',
      },
    },
  },
  payslips: {
    name: { singular: 'busta paga', plural: 'buste paga' },
    article: { singular: 'la', plural: 'le' },
    messages: {
      success: {
        created: 'Busta paga caricata con successo',
        updated: 'Busta paga aggiornata con successo',
        deleted: 'Busta paga eliminata con successo',
      },
      error: {
        notFound: 'La busta paga non è stata trovata',
        conflict: 'Una busta paga per questo periodo è già presente',
        validation: 'I dati della busta paga non sono validi',
      },
    },
  },
  leaveRequests: {
    name: { singular: 'richiesta ferie', plural: 'richieste ferie' },
    article: { singular: 'la', plural: 'le' },
    messages: {
      success: {
        created: 'Richiesta ferie inviata con successo',
        updated: 'Richiesta ferie aggiornata con successo',
        deleted: 'Richiesta ferie annullata con successo',
      },
      error: {
        notFound: 'La richiesta ferie non è stata trovata',
        conflict: 'Una richiesta ferie per questo periodo è già presente',
        validation: 'I dati della richiesta ferie non sono validi',
      },
    },
  },
  documents: {
    name: { singular: 'documento', plural: 'documenti' },
    article: { singular: 'il', plural: 'i' },
    messages: {
      success: {
        created: 'Documento caricato con successo',
        updated: 'Documento aggiornato con successo',
        deleted: 'Documento eliminato con successo',
      },
      error: {
        notFound: 'Il documento non è stato trovato',
        conflict: 'Un documento con questo nome è già presente',
        validation: 'I dati del documento non sono validi',
      },
    },
  },
  categories: {
    name: { singular: 'categoria', plural: 'categorie' },
    article: { singular: 'la', plural: 'le' },
    messages: {
      success: {
        created: 'Categoria creata con successo',
        updated: 'Categoria aggiornata con successo',
        deleted: 'Categoria eliminata con successo',
      },
      error: {
        notFound: 'La categoria non è stata trovata',
        conflict: 'Una categoria con questo codice è già presente',
        validation: 'I dati della categoria non sono validi',
      },
    },
  },
  bacheca: {
    name: { singular: 'comunicazione', plural: 'comunicazioni' },
    article: { singular: 'la', plural: 'le' },
    messages: {
      success: {
        created: 'Comunicazione pubblicata con successo',
        updated: 'Comunicazione aggiornata con successo',
        deleted: 'Comunicazione eliminata con successo',
      },
      error: {
        notFound: 'La comunicazione non è stata trovata',
        conflict: 'Una comunicazione con questo titolo è già presente',
        validation: 'I dati della comunicazione non sono validi',
      },
    },
  },
  settings: {
    name: { singular: 'impostazione', plural: 'impostazioni' },
    article: { singular: 'l\'', plural: 'le' },
    messages: {
      success: {
        created: 'Impostazione salvata con successo',
        updated: 'Impostazioni salvate con successo',
        deleted: 'Impostazione rimossa con successo',
      },
      error: {
        notFound: 'L\'impostazione non è stata trovata',
        conflict: 'Conflitto nel salvataggio delle impostazioni',
        validation: 'I dati delle impostazioni non sono validi',
      },
    },
  },
} as const satisfies Record<string, EntityConfig>;

/** Type-safe key for registered entities */
export type EntityKey = keyof typeof ENTITY_REGISTRY;
